import React, { useEffect, useState } from "react";
import supabase from "../../../supabaseClient";
import {
	Box,
	Button,
	TextField,
	MenuItem,
	Typography,
	CircularProgress,
	Paper,
	Modal,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from "@mui/material";
import axios from "axios";
import { formatEggQuantity } from "../../../utils/eggQuantity";

const RequestEggs = () => {
	const [requests, setRequests] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [createPopoverOpen, setCreatePopoverOpen] = useState(false);
	const [pickedUpModal, setPickedUpModal] = useState({
		open: false,
		requestId: null,
	});
	const [pickedUp, setPickedUp] = useState(false);
	const [editing, setEditing] = useState(false);

	const [editingId, setEditingId] = useState(null);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [userHasRegistered, setUserHasRegistered] = useState(false);
	const [isElevatedOrdering, setIsElevatedOrdering] = useState(false);
	const [editForm, setEditForm] = useState({
		chicken_eggs_requested: 0,
		duck_eggs_requested: 0,
		notes: "",
	});

	const [cancelModal, setCancelModal] = useState({
		open: false,
		requestId: null,
		loading: false,
		cancellationReason: "",
	});
	const [editPopoverOpen, setEditPopoverOpen] = useState(false);
	const [requestFilter, setRequestFilter] = useState("open");

	const [chickenEggs, setChickenEggs] = useState(0);
	const [duckEggs, setDuckEggs] = useState(0);
	const [notes, setNotes] = useState("");
	const [fulfillmentType, setFulfillmentType] = useState("pickup");

	const OPEN_STATUSES = ["pending", "awaiting_pickup", "awaiting_delivery"];

	const CLOSED_STATUSES = ["completed", "cancelled"];

	const fetchRequests = async () => {
		setLoading(true);
		setError("");
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setError("You must be logged in.");
			setLoading(false);
			return;
		}
		try {
			const { data: eggListData, error: eggListError } = await supabase
				.from("egg_list")
				.select("*")
				.eq("supabaseId", session.user.id);
			if (eggListError) {
				setError(eggListError.message);
				setLoading(false);
				return;
			}
			if (eggListData.length === 0) {
				setUserHasRegistered(false);
				setLoading(false);
				return;
			} else {
				setUserHasRegistered(true);
				setIsElevatedOrdering(!!eggListData[0].elevated_ordering);
			}

			const { data, error } = await supabase
				.from("egg_requests")
				.select("*")
				.eq("customer_id", session.user.id)
				.order("requested_at", { ascending: false });
			if (error) setError(error.message);
			else setRequests(data);
		} catch (err) {
			setError("Failed to fetch requests.");
		}
		setLoading(false);
	};

	useEffect(() => {
		fetchRequests();
		// eslint-disable-next-line
	}, []);

	const handlePickedUp = (id) => {
		setPickedUpModal({ open: true, requestId: id });
	};

	const setAsPickedUp = async (id) => {
		setLoading(true);
		setPickedUp(true);
		setError("");
		setSuccess("");
		try {
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				setError("You must be logged in.");
				setLoading(false);
				setPickedUp(false);
				return;
			}

			const response = await fetch(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/mark-egg-request-picked-up`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify({ request_id: id }),
				},
			);
			const result = await response.json();
			if (response.ok && result.message) {
				setSuccess("Marked as picked up.");
				await fetchRequests();
			} else {
				setError(result.error || "Failed to mark as picked up.");
			}
		} catch (err) {
			setError("Failed to mark as picked up.");
		}
		setLoading(false);
		setPickedUp(false);
	};

	const handleEditChange = (e) => {
		setEditForm((f) => ({ ...f, [e.target.name]: e.target.value }));
	};

	const handleEditPopoverClose = async () => {
		setEditingId(null);
		setEditPopoverOpen(false);
		await fetchRequests();
	};

	const handleEditSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		setEditing(true);
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setError("You must be logged in.");
			setEditing(false);
			return;
		}
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/request-eggs`,
				{
					...editForm,
					request_id: editingId,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200 && response.data.success) {
				setSuccess(response.data.message || "Request updated!");
				setTimeout(async () => {
					setEditingId(null);
					setEditPopoverOpen(false);
					setSuccess(null);
					await fetchRequests();
				}, 1000);
			} else {
				setError(response.data.error || "Failed to update request.");
			}
		} catch (err) {
			setError("Failed to update request.");
		}
		setEditing(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const maxEggs = isElevatedOrdering ? 120 : 60;
		if (chickenEggs + duckEggs > maxEggs) {
			alert(
				`Total eggs cannot exceed ${isElevatedOrdering ? "10" : "5"} dozen.`,
			);
			return;
		}
		setError("");
		setSuccess("");
		setSubmitting(true);
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setError("You must be logged in.");
			setSubmitting(false);
			return;
		}
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/request-eggs`,
				{
					chicken_eggs_requested: chickenEggs,
					duck_eggs_requested: duckEggs,
					notes: notes,
					request_id: editingId,
					fulfillment_type: isElevatedOrdering ? fulfillmentType : "pickup",
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200 && response.data.success) {
				setSuccess(response.data.message || "Request submitted!");
				setChickenEggs(0);
				setDuckEggs(0);
				setFulfillmentType("pickup");
				setEditingId(null);
				await fetchRequests();
				setCreatePopoverOpen(false);
			} else {
				setError(response.data.error || "Failed to submit request.");
			}
		} catch (err) {
			setError("Failed to submit request.");
		}
		setSubmitting(false);
	};

	const handleEdit = (req) => {
		setEditForm({
			chicken_eggs_requested: req.chicken_eggs_requested || 0,
			duck_eggs_requested: req.duck_eggs_requested || 0,
			notes: req.notes || "",
		});
		setEditingId(req.id);
		setEditPopoverOpen(true);
		setSuccess("");
		setError("");
	};

	const handleCancel = (id) => {
		setCancelModal({
			open: true,
			requestId: id,
			loading: false,
			cancellationReason: "",
		});
	};

	// Add a function to actually perform the cancel
	const confirmCancel = async () => {
		setCancelModal((prev) => ({ ...prev, loading: true }));
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setError("You must be logged in.");
			setCancelModal({
				open: false,
				requestId: null,
				loading: false,
				cancellationReason: "",
			});
			return;
		}
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/request-eggs`,
				{
					request_id: cancelModal.requestId,
					action: "cancel",
					cancellation_reason: cancelModal.cancellationReason,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);
			if (response.status === 200 && response.data.success) {
				setSuccess("Request cancelled.");
				await fetchRequests();
			} else {
				setError(response.data.error || "Failed to cancel request.");
			}
		} catch (err) {
			setError("Failed to cancel request.");
		}
		setCancelModal({
			open: false,
			requestId: null,
			loading: false,
			cancellationReason: "",
		});
	};

	const filteredRequests = requests?.filter((req) => {
		if (requestFilter === "open") {
			return OPEN_STATUSES.includes(req.request_status);
		}

		if (requestFilter === "closed") {
			return CLOSED_STATUSES.includes(req.request_status);
		}

		return true;
	});

	return loading ? (
		<Box sx={{ textAlign: "center", mt: 4 }}>
			<CircularProgress />
		</Box>
	) : (
		<Box sx={{ maxWidth: 500, mx: "auto", mt: 4 }}>
			{userHasRegistered ? (
				<Box sx={{ mb: 4 }}>
					<Button
						variant="contained"
						onClick={() => setCreatePopoverOpen(true)}
					>
						Request Eggs
					</Button>
				</Box>
			) : (
				<Box sx={{ mb: 4 }}>
					<Typography variant="h5" mb={2}>
						You must register for the egg list to request eggs.
					</Typography>
					<Typography variant="body1" mb={2}>
						Please visit the{" "}
						<a href="/egg-request-list">Egg Request Registration</a> page to
						sign up.
					</Typography>
				</Box>
			)}

			{userHasRegistered && (
				<Box mt={4}>
					<Typography variant="h6" mb={1}>
						Your Requests
					</Typography>
					<ToggleButtonGroup
						value={requestFilter}
						exclusive
						onChange={(_, value) => value && setRequestFilter(value)}
						aria-label="request-filter"
						size="small"
						sx={{ mb: 2 }}
					>
						<ToggleButton value="open">Open Orders</ToggleButton>

						<ToggleButton value="closed">Closed </ToggleButton>
					</ToggleButtonGroup>
					{loading ? (
						<CircularProgress />
					) : filteredRequests.length > 0 ? (
						filteredRequests.map((req) => (
							<Paper key={req.id} sx={{ p: 2, mb: 2 }}>
								<Typography>
									<b>Chicken Eggs Requested:</b>{" "}
									{formatEggQuantity(req.chicken_eggs_requested)}
								</Typography>
								<Typography>
									<b>Duck Eggs Requested:</b>{" "}
									{formatEggQuantity(req.duck_eggs_requested)}
								</Typography>
								<Typography>
									<b>Fulfillment:</b>{" "}
									{req.fulfillment_type === "delivery"
										? "Delivery to business"
										: "Pickup at farm"}
								</Typography>
								<Typography>
									<b>Status:</b>{" "}
									{req.request_status === "pending"
										? "Pending"
										: req.request_status === "completed"
											? "Completed"
											: req.request_status === "awaiting_pickup"
												? "Awaiting Pickup"
												: req.request_status === "awaiting_delivery"
													? "Awaiting Delivery"
													: req.request_status === "cancelled"
														? "Cancelled"
														: req.request_status}
								</Typography>
								<Typography>
									<b>Requested:</b>{" "}
									{new Date(req.requested_at).toLocaleString()}
								</Typography>
								{req.notes && (
									<Typography>
										<b>Notes:</b> {req.notes}
									</Typography>
								)}
								{req.request_status === "awaiting_delivery" &&
									req.estimated_delivery_date && (
										<Typography sx={{ color: "purple" }}>
											<b>Estimated Delivery:</b>{" "}
											{new Date(
												req.estimated_delivery_date + "T00:00:00",
											).toLocaleDateString()}
										</Typography>
									)}
								{req.request_status === "cancelled" &&
									req.cancellation_reason && (
										<Typography>
											<b>Cancellation Reason:</b> {req.cancellation_reason}
										</Typography>
									)}
								{req.request_status === "pending" && (
									<Box mt={1}>
										<Button
											size="small"
											variant="outlined"
											sx={{ mr: 1 }}
											onClick={() => handleEdit(req)}
										>
											Edit
										</Button>
										<Button
											size="small"
											variant="outlined"
											color="error"
											onClick={() => handleCancel(req.id)}
										>
											Cancel
										</Button>
									</Box>
								)}
								{req.request_status === "awaiting_pickup" && (
									<Box mt={1}>
										<Typography sx={{ mt: 1, color: "green" }}>
											Ready at: {new Date(req.fulfilled_at).toLocaleString()}
										</Typography>
										{
											//add a button to cancel
										}
										<Box //flex buttons
											display={"flex"}
											justifyContent={"flex-start"}
											alignItems={"center"}
											mt={1}
										>
											<Button
												size="small"
												variant="outlined"
												color="error"
												sx={{ mr: 1 }}
												onClick={() => handleCancel(req.id)}
											>
												Cancel
											</Button>

											<Button
												size="small"
												variant="outlined"
												color="primary"
												onClick={() => handlePickedUp(req.id)}
											>
												Mark as Picked Up
											</Button>
										</Box>
									</Box>
								)}
							</Paper>
						))
					) : (
						<Typography>No requests found.</Typography>
					)}
				</Box>
			)}
			{editPopoverOpen && (
				<Box
					sx={{
						position: "fixed",
						zIndex: 1200,
						top: 0,
						left: 0,
						width: "100vw",
						height: "100vh",
						bgcolor: "rgba(255,255,255,0.3)",
						backdropFilter: "blur(6px)",
					}}
				/>
			)}
			{createPopoverOpen && (
				<Box
					sx={{
						position: "fixed",
						zIndex: 1200,
						top: 0,
						left: 0,
						width: "100vw",
						height: "100vh",
						bgcolor: "rgba(255,255,255,0.3)",
						backdropFilter: "blur(6px)",
					}}
				/>
			)}
			<Modal
				open={pickedUpModal.open}
				onClose={async () => {
					setPickedUpModal({ open: false, requestId: null });
					await fetchRequests();
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 300,
						bgcolor: "background.paper",
						border: "2px solid #000",
						boxShadow: 24,
						p: 4,
					}}
				>
					<Typography variant="h6" mb={2}>
						Confirm Pickup
					</Typography>
					<Typography mb={2}>
						Are you sure you want to mark this request as picked up?
					</Typography>
					<Button
						variant="contained"
						color="primary"
						onClick={async () => {
							await setAsPickedUp(pickedUpModal.requestId);
							setPickedUpModal({ open: false, requestId: null });
							await fetchRequests();
						}}
						disabled={pickedUp}
						sx={{ mr: 1 }}
					>
						{pickedUp ? (
							<CircularProgress size={24} />
						) : (
							"Yes, Mark as Picked Up"
						)}
					</Button>
					<Button
						variant="outlined"
						onClick={async () => {
							setPickedUpModal({ open: false, requestId: null });
							await fetchRequests();
						}}
					>
						Cancel
					</Button>
				</Box>
			</Modal>
			<Modal
				open={editPopoverOpen}
				onClose={handleEditPopoverClose}
				anchorOrigin={{
					vertical: "center",
					horizontal: "center",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 300,
						bgcolor: "background.paper",
						border: "2px solid #000",
						boxShadow: 24,
						p: 4,
					}}
				>
					<Typography variant="h6" mb={2}>
						Edit Request
					</Typography>
					{editing ? (
						<CircularProgress />
					) : (
						<form onSubmit={handleEditSubmit}>
							<TextField
								label="Number of Chicken Eggs"
								type="number"
								name="chicken_eggs_requested"
								value={editForm.chicken_eggs_requested || 0}
								onChange={handleEditChange}
								fullWidth
								sx={{ mb: 2 }}
								select
							>
								<MenuItem value={0}>0 chicken eggs</MenuItem>
								<MenuItem value={12}>1 dozen</MenuItem>
								<MenuItem value={24}>2 dozen</MenuItem>
								<MenuItem value={36}>3 dozen</MenuItem>
								<MenuItem value={48}>4 dozen</MenuItem>
								<MenuItem value={60}>5 dozen</MenuItem>
								{isElevatedOrdering && <MenuItem value={72}>6 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={84}>7 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={96}>8 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={108}>9 dozen</MenuItem>}
								{isElevatedOrdering && (
									<MenuItem value={120}>10 dozen</MenuItem>
								)}
							</TextField>
							<TextField
								label="Number of Duck Eggs"
								type="number"
								name="duck_eggs_requested"
								value={editForm.duck_eggs_requested || 0}
								onChange={handleEditChange}
								fullWidth
								sx={{ mb: 2 }}
								select
							>
								<MenuItem value={0}>0 duck eggs</MenuItem>
								{!isElevatedOrdering && (
									<MenuItem value={6}>1/2 dozen</MenuItem>
								)}
								<MenuItem value={12}>1 dozen</MenuItem>
								{!isElevatedOrdering && (
									<MenuItem value={18}>1 1/2 dozen</MenuItem>
								)}
								<MenuItem value={24}>2 dozen</MenuItem>
								{!isElevatedOrdering && (
									<MenuItem value={30}>2 1/2 dozen</MenuItem>
								)}
								<MenuItem value={36}>3 dozen</MenuItem>
								<MenuItem value={48}>4 dozen</MenuItem>
								<MenuItem value={60}>5 dozen</MenuItem>
								{isElevatedOrdering && <MenuItem value={72}>6 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={84}>7 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={96}>8 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={108}>9 dozen</MenuItem>}
								{isElevatedOrdering && (
									<MenuItem value={120}>10 dozen</MenuItem>
								)}
							</TextField>

							<TextField
								label="Notes (optional)"
								name="notes"
								value={editForm.notes}
								onChange={handleEditChange}
								fullWidth
								sx={{ mb: 2 }}
							/>
							<Button
								type="submit"
								variant="contained"
								fullWidth
								disabled={loading}
							>
								Update Request
							</Button>
							{error && (
								<Typography color="error" mt={2}>
									{error}
								</Typography>
							)}
							{success && (
								<Typography color="success.main" mt={2}>
									{success}
								</Typography>
							)}
						</form>
					)}
				</Box>
			</Modal>
			<Modal
				open={createPopoverOpen}
				onClose={() => {
					setCreatePopoverOpen(false);
					setFulfillmentType("pickup");
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: {
							xs: "80%",
							sm: "70%",
							md: "50%",
						},
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
					}}
				>
					<Typography variant="h6" mb={2}>
						Request Eggs
					</Typography>
					{submitting ? (
						<CircularProgress />
					) : (
						<form onSubmit={handleSubmit}>
							{isElevatedOrdering && (
								<TextField
									label="Fulfillment Type"
									select
									fullWidth
									value={fulfillmentType}
									onChange={(e) => setFulfillmentType(e.target.value)}
									sx={{ mb: 2 }}
								>
									<MenuItem value="pickup">Pickup at the farm</MenuItem>
									<MenuItem value="delivery">Delivery to my business</MenuItem>
								</TextField>
							)}
							<TextField
								label="Chicken Eggs"
								type="number"
								value={chickenEggs || 0}
								onChange={(e) =>
									setChickenEggs(Math.max(0, parseInt(e.target.value) || 0))
								}
								fullWidth
								select
								required
								sx={{ mb: 2 }}
							>
								<MenuItem value={0}>0 chicken eggs</MenuItem>
								<MenuItem value={12}>1 dozen</MenuItem>
								<MenuItem value={24}>2 dozen</MenuItem>
								<MenuItem value={36}>3 dozen</MenuItem>
								<MenuItem value={48}>4 dozen</MenuItem>
								<MenuItem value={60}>5 dozen</MenuItem>
								{isElevatedOrdering && <MenuItem value={72}>6 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={84}>7 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={96}>8 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={108}>9 dozen</MenuItem>}
								{isElevatedOrdering && (
									<MenuItem value={120}>10 dozen</MenuItem>
								)}
							</TextField>
							<TextField
								label="Duck Eggs"
								type="number"
								value={duckEggs || 0}
								onChange={(e) =>
									setDuckEggs(Math.max(0, parseInt(e.target.value) || 0))
								}
								fullWidth
								select
								required
								sx={{ mb: 2 }}
							>
								<MenuItem value={0}>0 duck eggs</MenuItem>
								{!isElevatedOrdering && (
									<MenuItem value={6}>1/2 dozen</MenuItem>
								)}
								<MenuItem value={12}>1 dozen</MenuItem>
								{!isElevatedOrdering && (
									<MenuItem value={18}>1 1/2 dozen</MenuItem>
								)}
								<MenuItem value={24}>2 dozen</MenuItem>
								{!isElevatedOrdering && (
									<MenuItem value={30}>2 1/2 dozen</MenuItem>
								)}
								<MenuItem value={36}>3 dozen</MenuItem>
								<MenuItem value={48}>4 dozen</MenuItem>
								<MenuItem value={60}>5 dozen</MenuItem>
								{isElevatedOrdering && <MenuItem value={72}>6 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={84}>7 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={96}>8 dozen</MenuItem>}
								{isElevatedOrdering && <MenuItem value={108}>9 dozen</MenuItem>}
								{isElevatedOrdering && (
									<MenuItem value={120}>10 dozen</MenuItem>
								)}
							</TextField>
							<TextField
								label="Notes (optional)"
								name="notes"
								value={notes}
								rows={2}
								multiline
								onChange={(e) => setNotes(e.target.value)}
								fullWidth
								sx={{ mb: 2 }}
							/>
							<Typography
								variant="body2"
								sx={{
									mb: 2,
									color:
										chickenEggs + duckEggs > (isElevatedOrdering ? 120 : 60) ||
										chickenEggs % 12 !== 0 ||
										(isElevatedOrdering
											? duckEggs % 12 !== 0
											: duckEggs % 6 !== 0)
											? "error.main"
											: "text.primary",
								}}
							>
								Total: {formatEggQuantity(chickenEggs + duckEggs)} /{" "}
								{formatEggQuantity(isElevatedOrdering ? 120 : 60)}
							</Typography>
							{chickenEggs + duckEggs > (isElevatedOrdering ? 120 : 60) ||
							chickenEggs % 12 !== 0 ||
							(isElevatedOrdering
								? duckEggs % 12 !== 0
								: duckEggs % 6 !== 0) ? (
								<Tooltip
									title={`Total eggs must not exceed ${isElevatedOrdering ? "10" : "5"} dozen.`}
								>
									<span>
										<Button
											type="submit"
											variant="contained"
											fullWidth
											disabled={
												submitting ||
												chickenEggs + duckEggs >
													(isElevatedOrdering ? 120 : 60) ||
												chickenEggs % 12 !== 0 ||
												(isElevatedOrdering
													? duckEggs % 12 !== 0
													: duckEggs % 6 !== 0)
											}
										>
											Submit Request
										</Button>
									</span>
								</Tooltip>
							) : (
								<Button
									type="submit"
									variant="contained"
									fullWidth
									disabled={
										submitting ||
										chickenEggs + duckEggs > (isElevatedOrdering ? 120 : 60) ||
										chickenEggs % 12 !== 0 ||
										(isElevatedOrdering
											? duckEggs % 12 !== 0
											: duckEggs % 6 !== 0)
									}
								>
									Submit Request
								</Button>
							)}
							{error && (
								<Typography color="error" mt={2}>
									{error}
								</Typography>
							)}
							{success && (
								<Typography color="success.main" mt={2}>
									{success}
								</Typography>
							)}
						</form>
					)}
				</Box>
			</Modal>
			<Modal
				open={cancelModal.open}
				onClose={() =>
					setCancelModal({
						open: false,
						requestId: null,
						loading: false,
						cancellationReason: "",
					})
				}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 300,
						bgcolor: "background.paper",
						border: "2px solid #000",
						boxShadow: 24,
						p: 4,
					}}
				>
					<Typography variant="h6" mb={2}>
						Confirm Cancel
					</Typography>
					<Typography mb={2}>
						Are you sure you want to cancel this request?
					</Typography>
					<TextField
						label="Cancellation Reason (optional)"
						fullWidth
						multiline
						minRows={2}
						value={cancelModal.cancellationReason}
						onChange={(e) =>
							setCancelModal((prev) => ({
								...prev,
								cancellationReason: e.target.value,
							}))
						}
						sx={{ mb: 2 }}
					/>
					<Box display={"flex"}>
						<Button
							variant="contained"
							color="error"
							onClick={confirmCancel}
							disabled={cancelModal.loading}
							sx={{ mr: 1 }}
						>
							{cancelModal.loading ? (
								<CircularProgress size={24} />
							) : (
								"Cancel Request"
							)}
						</Button>
						<Button
							variant="outlined"
							onClick={() =>
								setCancelModal({
									open: false,
									requestId: null,
									loading: false,
									cancellationReason: "",
								})
							}
						>
							Go Back
						</Button>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
};

export default RequestEggs;
